import { N8AOPass, type N8AODisplayMode, type N8AOQualityMode } from "n8ao";
import type { Camera, Scene } from "three";
import { Emitter } from "@core/events/emitter";
import { isMobileDevice } from "@runtime/browser/mobileDevice";

/**
 * Screen-space ambient occlusion for the viewport, via N8AO
 * (https://github.com/N8python/n8ao, `n8ao` on npm).
 *
 * Campfire has no upstream Unity counterpart for this — the desktop app gets
 * its contact shading from baked lighting, so this is a WEB-FIRST surface and
 * the numbers below are judgement calls, not a port.
 *
 * ## Why the pass is not always in the chain
 *
 * `N8AOPass` REPLACES `RenderPass` (it renders the scene itself, into its own
 * target, so it owns the depth buffer it samples). `SelectionOutlineController`
 * therefore keeps BOTH in the composer and enables exactly one — see
 * `applyAmbientOcclusionSettings`. Enabling both would render the scene twice
 * and the AO would composite over a stale frame.
 *
 * ## Desktop only
 *
 * A headset never runs this: `EngineService.renderFrame` bypasses the composer
 * outright while `renderer.xr.isPresenting`, so an XR session cannot reach the
 * pass at all. {@link isAmbientOcclusionSupported} covers the two cases that
 * bypass does NOT — the Quest browser's flat 2D mode, and mobile devices —
 * where the composer still runs but the GPU budget is not there for a
 * full-screen depth-aware effect.
 *
 * ## Color
 *
 * `gammaCorrection` MUST be false. Read `COLOR_PIPELINE.md` before changing
 * anything here: the composer renders LINEAR radiance and applies the sRGB
 * transfer ONCE, in the `OutputPass` at the end of the chain. N8AO's default
 * is to sRGB-encode its own output, which would encode this pass twice.
 */

export const AO_QUALITY_MODES = ["Performance", "Low", "Medium", "High", "Ultra"] as const;
export type AoQuality = (typeof AO_QUALITY_MODES)[number];

/**
 * `Split AO` is deliberately omitted — five view modes in a dev panel is a
 * menu, and the AO/Combined pair plus the split wipe covers what tuning needs.
 */
export const AO_DISPLAY_MODES = ["Combined", "AO", "No AO", "Split"] as const;
export type AoDisplayMode = (typeof AO_DISPLAY_MODES)[number];

export interface AmbientOcclusionSettings {
  readonly enabled: boolean;
  /** `pow(ao, intensity)` — artistic darkening, not a physical quantity. */
  readonly intensity: number;
  /** Pixels when {@link screenSpaceRadius}, else world units (metres). */
  readonly radius: number;
  /** Fraction of the radius at which occlusion fades out. */
  readonly falloff: number;
  readonly screenSpaceRadius: boolean;
  readonly halfRes: boolean;
  readonly quality: AoQuality;
  readonly displayMode: AoDisplayMode;
}

/**
 * `screenSpaceRadius` is ON by default, which is NOT N8AO's default, because
 * Campfire project scale spans orders of magnitude — a robot dog is under a
 * metre, an Apollo hatch is two, a machine cell is ten. A world-space radius
 * that reads correctly on one of those is an edge detector on the next, and
 * there is no per-project place to store a tuned value. In screen-space mode
 * the radius is in pixels and the look holds across all of them; the trade is
 * that occlusion grows as you dolly in, which reads acceptably because the
 * effect is contact shading rather than a light.
 *
 * The rest were dialled by eye against Robodog on 2026-08-18 and are NOT the
 * library's suggestions: N8AO's docs give 0.2 for screen-space `falloff`, and
 * 1.0 — occlusion fading across the whole radius rather than a fifth of it —
 * is what stopped the effect reading as a hard contact ring at this radius.
 * A 138 px radius is very broad on purpose; the Campfire look wanted ambient
 * shaping rather than tight creases. Re-dial rather than re-derive.
 *
 * `halfRes` with the `Low` preset is the other half of that choice and is
 * what makes the wide radius affordable: a large, soft, low-frequency
 * occlusion field is the case that survives being computed at quarter the
 * pixels and 16 samples, because there is no fine detail for the upsample to
 * lose. Narrowing the radius later without also raising quality would expose
 * the sample count as visible noise.
 */
export const AO_DEFAULTS: AmbientOcclusionSettings = {
  enabled: true,
  intensity: 3,
  radius: 138,
  falloff: 1,
  screenSpaceRadius: true,
  halfRes: true,
  quality: "Low",
  displayMode: "Combined",
};

export const AO_INTENSITY_RANGE = { min: 0, max: 10, step: 0.1 } as const;
/**
 * The radius control changes UNIT with the mode, so it changes range too.
 * The screen-space ceiling was raised from 96 when 96 became the default —
 * a slider whose default is its own maximum can only ever be turned down.
 */
export const AO_SCREEN_RADIUS_RANGE = { min: 4, max: 192, step: 1 } as const;
export const AO_WORLD_RADIUS_RANGE = { min: 0.01, max: 5, step: 0.01 } as const;
export const AO_SCREEN_FALLOFF_RANGE = { min: 0.02, max: 1, step: 0.02 } as const;
export const AO_WORLD_FALLOFF_RANGE = { min: 0.1, max: 5, step: 0.1 } as const;

const K_PREF_KEY = "Campfire.Dev.AmbientOcclusion";

/**
 * Whether this device should run AO at all.
 *
 * NOT expressed through `XRSessionService.isHeadsetBrowser`, which is the
 * right predicate for auto-entering XR and the wrong one here: it is built on
 * `isSafariLikeBrowser()` and so matches DESKTOP Safari, which would lose the
 * effect for no reason. The Quest browser is matched directly instead.
 */
export function isAmbientOcclusionSupported(): boolean {
  return !isMobileDevice();
}

export class AmbientOcclusionModel {
  readonly onChanged = new Emitter<AmbientOcclusionSettings>();

  private m_settings: AmbientOcclusionSettings = readPref();

  get settings(): AmbientOcclusionSettings {
    return this.m_settings;
  }

  update(patch: Partial<AmbientOcclusionSettings>): void {
    const next = clampSettings({ ...this.m_settings, ...patch });
    if (isSameSettings(next, this.m_settings)) return;
    this.m_settings = next;
    writePref(next);
    this.onChanged.emit(next);
  }

  reset(): void {
    this.update(AO_DEFAULTS);
  }
}

export const AMBIENT_OCCLUSION_MODEL = Symbol.for("Campfire.AmbientOcclusionModel");

export function createAmbientOcclusionPass(
  scene: Scene,
  camera: Camera,
  width: number,
  height: number,
): N8AOPass {
  const pass = new N8AOPass(scene, camera, width, height);
  // See the color note in the file header — this one is not optional.
  pass.configuration.gammaCorrection = false;
  return pass;
}

/**
 * Pushes `settings` onto the pass and reports whether AO should own the frame
 * (the caller flips `RenderPass.enabled` to the inverse).
 *
 * Writes are guarded on inequality because `configuration` is a `Proxy` whose
 * setter rebuilds render targets and recompiles shaders for `aoSamples`,
 * `denoiseSamples` and `halfRes`. Assigning the same value every frame — or
 * every slider tick on an unrelated control — would stall the frame.
 */
export function applyAmbientOcclusionSettings(
  pass: N8AOPass,
  settings: AmbientOcclusionSettings,
): void {
  const config = pass.configuration;
  if (config.intensity !== settings.intensity) config.intensity = settings.intensity;
  if (config.aoRadius !== settings.radius) config.aoRadius = settings.radius;
  if (config.distanceFalloff !== settings.falloff) config.distanceFalloff = settings.falloff;
  if (config.screenSpaceRadius !== settings.screenSpaceRadius) {
    config.screenSpaceRadius = settings.screenSpaceRadius;
  }
  if (config.halfRes !== settings.halfRes) config.halfRes = settings.halfRes;

  const quality = settings.quality satisfies N8AOQualityMode;
  if (qualityOf(pass) !== quality) pass.setQualityMode(quality);

  const displayMode = settings.displayMode satisfies N8AODisplayMode;
  const renderMode = AO_DISPLAY_MODES.indexOf(settings.displayMode);
  if (config.renderMode !== renderMode) pass.setDisplayMode(displayMode);
}

/**
 * N8AO exposes the quality preset only as the three sample counts it expands
 * to, so the preset has to be recovered from them to avoid a recompile on
 * every unrelated settings change.
 */
function qualityOf(pass: N8AOPass): AoQuality | null {
  const { aoSamples, denoiseSamples, denoiseRadius } = pass.configuration;
  for (const mode of AO_QUALITY_MODES) {
    const preset = K_QUALITY_PRESETS[mode];
    if (
      preset.aoSamples === aoSamples &&
      preset.denoiseSamples === denoiseSamples &&
      preset.denoiseRadius === denoiseRadius
    ) {
      return mode;
    }
  }
  return null;
}

/** Verbatim from `N8AOPass.setQualityMode`; kept in sync by {@link qualityOf}. */
const K_QUALITY_PRESETS: Readonly<
  Record<AoQuality, { aoSamples: number; denoiseSamples: number; denoiseRadius: number }>
> = {
  Performance: { aoSamples: 8, denoiseSamples: 4, denoiseRadius: 12 },
  Low: { aoSamples: 16, denoiseSamples: 4, denoiseRadius: 12 },
  Medium: { aoSamples: 16, denoiseSamples: 8, denoiseRadius: 12 },
  High: { aoSamples: 64, denoiseSamples: 8, denoiseRadius: 6 },
  Ultra: { aoSamples: 64, denoiseSamples: 16, denoiseRadius: 6 },
};

function clampSettings(s: AmbientOcclusionSettings): AmbientOcclusionSettings {
  const radiusRange = s.screenSpaceRadius ? AO_SCREEN_RADIUS_RANGE : AO_WORLD_RADIUS_RANGE;
  const falloffRange = s.screenSpaceRadius ? AO_SCREEN_FALLOFF_RANGE : AO_WORLD_FALLOFF_RANGE;
  return {
    enabled: s.enabled,
    intensity: clamp(s.intensity, AO_INTENSITY_RANGE, AO_DEFAULTS.intensity),
    radius: clamp(s.radius, radiusRange, s.screenSpaceRadius ? AO_DEFAULTS.radius : 0.25),
    falloff: clamp(s.falloff, falloffRange, s.screenSpaceRadius ? AO_DEFAULTS.falloff : 1),
    screenSpaceRadius: s.screenSpaceRadius,
    halfRes: s.halfRes,
    quality: AO_QUALITY_MODES.includes(s.quality) ? s.quality : AO_DEFAULTS.quality,
    displayMode: AO_DISPLAY_MODES.includes(s.displayMode) ? s.displayMode : AO_DEFAULTS.displayMode,
  };
}

function clamp(value: number, range: { min: number; max: number }, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(range.max, Math.max(range.min, value));
}

function isSameSettings(a: AmbientOcclusionSettings, b: AmbientOcclusionSettings): boolean {
  return (
    a.enabled === b.enabled &&
    a.intensity === b.intensity &&
    a.radius === b.radius &&
    a.falloff === b.falloff &&
    a.screenSpaceRadius === b.screenSpaceRadius &&
    a.halfRes === b.halfRes &&
    a.quality === b.quality &&
    a.displayMode === b.displayMode
  );
}

/**
 * Absence is the default, and a PARTIAL stored object is merged over the
 * defaults rather than rejected — the shape grows as controls are added, and a
 * developer mid-experiment should not silently lose their tuning to a new key.
 */
function readPref(): AmbientOcclusionSettings {
  try {
    const raw = window.localStorage.getItem(K_PREF_KEY);
    if (!raw) return AO_DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return AO_DEFAULTS;
    return clampSettings({ ...AO_DEFAULTS, ...(parsed as Partial<AmbientOcclusionSettings>) });
  } catch {
    return AO_DEFAULTS;
  }
}

function writePref(settings: AmbientOcclusionSettings): void {
  try {
    if (isSameSettings(settings, AO_DEFAULTS)) window.localStorage.removeItem(K_PREF_KEY);
    else window.localStorage.setItem(K_PREF_KEY, JSON.stringify(settings));
  } catch {
    /* private-mode storage refusal is not worth surfacing */
  }
}
